<script setup lang="ts">
// Vue & Plugins
import { computed, inject, Ref } from 'vue'
import { useShiftConflictDialog } from '../composables/useShiftConflictDialog'
import { DateTime } from 'luxon'
import { useI18n } from 'vue-i18n'
import { useShiftQuery } from '../composables/useShiftQuery'
import { Shift } from '../models/shift'
import { Site } from '../../sites/models/site'
import { currentSiteKey } from '../../common/injection'
import ShiftConflictListItem from './ShiftConflictListItem.vue'
import { useEmployeeDetailQuery } from '../../employees/composables/useEmployeeQuery'

const site = inject(currentSiteKey) as Ref<Site>

const { hide, isShown, area, building, employeeId, skill } =
  useShiftConflictDialog()

const enabled = computed(
  () =>
    employeeId.value !== undefined &&
    employeeId.value !== null &&
    ((area.value !== undefined && area.value !== null) ||
      (building.value !== undefined && building.value !== null) ||
      (skill.value !== undefined && skill.value !== null)),
)

const {
  data: employeeData, 
  isFetching: isEmployeeFetching,
} = useEmployeeDetailQuery(
  () => site.value,
  () => employeeId.value,
)
const employee = computed(() => employeeData.value?.employee)

const { data, isFetching, refetch } = useShiftQuery(
  () => site.value.id,
  {
    area,
    building,
    employeeId,
    employeeOfferStatus: 'accepted',
    skill,
    status: 'active',
    fillStatus: 'filled',
    dateFrom: DateTime.now(),
  },
  { enabled },
)

const shifts = computed<Shift[]>(() => data.value?.shifts ?? [])

const { t } = useI18n()
</script>

<template>
  <Dialog
    :visible="isShown"
    header="Shift Conflicts"
    dismissable-mask
    modal
    class="w-1/2 max-w-4xl"
    @update:visible="hide"
  >
    <template #header>
      <div class="flex items-center justify-between">
        <div v-if="employee" class="flex items-center">
          <Avatar
            :label="employee.firstName.charAt(0) + employee.lastName.charAt(0)"
            :image="employee.avatarUrl"
            class="bg-highlight"
            shape="circle"
            size="large"
          />
          <div class="ml-4">
            <div class="font-medium">
              {{ employee.firstName }} {{ employee.lastName }}
            </div>
            <div class="text-muted-color">
              {{ employee.email }}
            </div>
          </div>
        </div>
        <div
          v-else-if="isEmployeeFetching"
        >
          <ProgressSpinner
            stroke-width="8"
            style="width: 24px; height: 24px;"
          />
        </div>
      </div>
    </template>

    <div class="m-1 flex flex-col gap-y-4">
      <Message v-if="shifts.length > 0" severity="info">
        <div class="flex flex-col gap-4">
          <div class="font-medium">
            {{ t('shifts.conflicts.help') }}
          </div>
        </div>
      </Message>

      <!--TODO: Use a virutal scroller to increase performance -->
      <ProgressBar v-if="isFetching" mode="indeterminate" style="height: 4px;" />

      <ul
        v-if="employee"
        ref="scroll-element"
        class="mt-4 grid grid-cols-1 gap-y-4 overflow-y-auto"
      >
        <li v-for="shift in shifts" :key="shift.id">
          <ShiftConflictListItem
            :site-id="site.id"
            :employee-id="employee.id"
            :shift="shift"
            @updated="refetch()"
          />
        </li>
      </ul>
    </div>
  </Dialog>
</template>
